// Custom-field measurement grammar — the shared vocabulary between the issue
// aggregation (`issueGroupCounts` in the core API) and every dashboard widget
// that reads it. Both sides import this file so a token the client builds is a
// token the server parses, and the type gates agree.
//
// Two ways a custom field enters an aggregation:
//   groupBy `values.<fieldId>`          one bucket per stored value
//   groupBy `values.<fieldId>.<grain>`  date fields: one bucket per period
//   measure { fieldId, fn }             the figure per bucket is an aggregate
//                                       of the field instead of a row count
// A field id is cuid-style (letters, digits; hyphens in fixtures) and never
// contains a dot, so the grain is whatever follows the second dot.

/** The `groupBy` prefix a custom-field dimension carries — the same prefix the
 *  issue filter vocabulary keys `values.<fieldId>` conditions under. */
export const FIELD_GROUP_PREFIX = "values.";

/** Field types a `groupBy` may bucket on. Select, labels and member persist
 *  arrays (a project lands in one bucket per value); checkbox and region are
 *  scalars; the date types need a grain. Free-text types are out — one bucket
 *  per typed answer is noise, not a chart. */
export const FIELD_GROUP_TYPES = [
  "select",
  "labels",
  "member",
  "checkbox",
  "region",
  "date",
  "dateTime",
] as const;
export type FieldGroupType = (typeof FIELD_GROUP_TYPES)[number];

/** The array-valued members of {@link FIELD_GROUP_TYPES}. */
export const FIELD_GROUP_ARRAY_TYPES = ["select", "labels", "member"] as const;

/** The date members of {@link FIELD_GROUP_TYPES} — a token for one carries a grain. */
export const FIELD_GROUP_DATE_TYPES = ["date", "dateTime"] as const;

/** Period widths a date field buckets by — the issue time-series grains. */
export const FIELD_DATE_GRAINS = ["day", "week", "month"] as const;
export type FieldDateGrain = (typeof FIELD_DATE_GRAINS)[number];

/** Field types a measure may aggregate. Both persist a JSON number. */
export const MEASURABLE_FIELD_TYPES = ["number", "money"] as const;

/**
 * The heading a picker files a field type under. Headings come from the type
 * table, never from any workspace's field names, so a picker reads the same in
 * a roofing company and a landscaping one. Types listed here and in neither
 * gate above still get a heading, for pickers that offer every field.
 */
export const FIELD_TYPE_HEADING: Record<string, string> = {
  select: "Single select",
  labels: "Multi select",
  checkbox: "Checkboxes",
  member: "People",
  region: "Regions",
  date: "Dates",
  dateTime: "Dates",
  money: "Money",
  number: "Numbers",
  text: "Text",
  paragraph: "Text",
  email: "Text",
  phone: "Text",
  url: "Text",
  address: "Addresses",
  photos: "Files",
  files: "Files",
  formula: "Formulas",
};

/** `FIELD_TYPE_HEADING` for a type, "Other" for one it does not list. */
export function fieldTypeHeading(type: string): string {
  return FIELD_TYPE_HEADING[type] ?? "Other";
}

/** The aggregate a measure applies per bucket. */
export const MEASURE_FNS = ["sum", "avg", "min", "max"] as const;
export type MeasureFn = (typeof MEASURE_FNS)[number];

export interface FieldGroupToken {
  fieldId: string;
  /** Present only for a date field. */
  grain: FieldDateGrain | null;
}

/** The `groupBy` token that counts every row as one bucket — a single figure. */
export const TOTAL_GROUP_BY = "total";
/** The one key the {@link TOTAL_GROUP_BY} answer carries. */
export const TOTAL_GROUP_KEY = "__all__";

const FIELD_ID = /^[A-Za-z0-9_-]+$/;

export function isFieldGroupToken(token: string): boolean {
  return token.startsWith(FIELD_GROUP_PREFIX);
}

/** Build a custom-field `groupBy` token. Pass `grain` for a date field only. */
export function fieldGroupToken(fieldId: string, grain?: FieldDateGrain | null): string {
  return grain ? `${FIELD_GROUP_PREFIX}${fieldId}.${grain}` : `${FIELD_GROUP_PREFIX}${fieldId}`;
}

/**
 * Parse a custom-field `groupBy` token. Returns null for anything that is not
 * `values.<fieldId>` or `values.<fieldId>.<grain>` — callers treat null as
 * "not a field token", never as an error.
 */
export function parseFieldGroupToken(token: string): FieldGroupToken | null {
  if (!token.startsWith(FIELD_GROUP_PREFIX)) return null;
  const parts = token.slice(FIELD_GROUP_PREFIX.length).split(".");
  const [fieldId, grain, extra] = parts;
  if (!fieldId || !FIELD_ID.test(fieldId) || extra !== undefined) return null;
  if (grain === undefined) return { fieldId, grain: null };
  return (FIELD_DATE_GRAINS as readonly string[]).includes(grain)
    ? { fieldId, grain: grain as FieldDateGrain }
    : null;
}

export function isMeasureFn(value: unknown): value is MeasureFn {
  return typeof value === "string" && (MEASURE_FNS as readonly string[]).includes(value);
}
